import React from 'react';
import {
  Box,
  Breadcrumbs,
  Card,
  Chip,
  Container,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Page from 'src/components/Page';

const colors = {
  bg: '#f4f7fb',
  surface: '#ffffff',
  ink: '#172033',
  muted: '#667085',
  line: '#e5eaf2',
  brand: '#1463ff',
  brandLight: '#55a5ff',
  uniview: '#8892a6',
  navy: '#102a56',
  softBlue: '#edf4ff',
  softGreen: '#eaf8f3',
  softOrange: '#fff4e8',
  chartTick: '#7a8598',
  chartLabel: '#526077',
};

const reportShadow = '0 10px 35px rgba(17, 37, 70, 0.08)';
const cardRadius = '18px';

const reportCardSx = {
  background: colors.surface,
  border: `1px solid ${colors.line}`,
  borderRadius: cardRadius,
  boxShadow: reportShadow,
  p: { xs: 2, md: 2.5 },
  height: '100%',
};

const badgeBaseSx = {
  borderRadius: '999px',
  height: 'auto',
  px: 1.25,
  py: 0.75,
  fontSize: 12,
  fontWeight: 750,
  '& .MuiChip-label': {
    p: 0,
  },
};

const ReportContainer = ({ children }) => (
  <Box
    sx={{
      maxWidth: 1180,
      mx: 'auto',
      px: { xs: 1.5, sm: 2.5 },
      py: { xs: 2, md: 3.5 },
      color: colors.ink,
      background: colors.bg,
    }}
  >
    {children}
  </Box>
);

const StyledCard = ({ children, sx }) => <Card sx={{ ...reportCardSx, ...sx }}>{children}</Card>;

const Badge = ({ label, variant = 'brand' }) => {
  const variantSx = {
    brand: { background: colors.softBlue, color: '#0b58e8' },
    good: { background: colors.softGreen, color: '#087e53' },
    neutral: { background: '#f0f2f6', color: '#535e72' },
  };

  return <Chip label={label} sx={{ ...badgeBaseSx, ...variantSx[variant] }} />;
};

const HeroSection = () => (
  <Box
    component="section"
    sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: { xs: '20px', md: '26px' },
      p: { xs: 2.5, sm: 3.5, md: 4.75 },
      color: '#fff',
      background:
        'radial-gradient(circle at 85% 20%, rgba(85,165,255,.35), transparent 28%), linear-gradient(135deg, #0d2148 0%, #123d78 58%, #1463ff 100%)',
      boxShadow: reportShadow,
      '&::after': {
        content: '""',
        position: 'absolute',
        width: 240,
        height: 240,
        border: '1px solid rgba(255,255,255,.14)',
        borderRadius: '50%',
        right: -70,
        bottom: -120,
      },
    }}
  >
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1.375,
        py: 0.875,
        border: '1px solid rgba(255,255,255,.22)',
        background: 'rgba(255,255,255,.08)',
        borderRadius: '999px',
        fontSize: 12,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
      }}
    >
      Client Benchmark Report
    </Box>

    <Typography
      component="h1"
      sx={{
        maxWidth: 820,
        mt: 2.25,
        mb: 1.25,
        fontSize: { xs: 30, sm: 40, md: 54 },
        lineHeight: 1.06,
        fontWeight: 800,
        color: '#fff',
      }}
    >
      Uniview Built-in Analytics vs Vidisky Analytics
    </Typography>

    <Typography
      sx={{
        maxWidth: 850,
        color: 'rgba(255,255,255,.84)',
        fontSize: { xs: 14, sm: 16 },
        lineHeight: 1.5,
      }}
    >
      Comparative evaluation across two 8 MP Uniview camera generations and two analytics modes:
      Intrusion Detection (person + vehicle combined) and Separate Person & Vehicle Detection.
    </Typography>

    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mt: 3 }}>
      {['161 ground-truth activities', '10 test hours', 'Day + night evaluation', 'Tri-Guard 1.0 + Tri-Guard 2.0'].map(
        (label) => (
          <Chip
            key={label}
            label={label}
            sx={{
              ...badgeBaseSx,
              px: 1.5,
              py: 1,
              color: '#fff',
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.14)',
              fontSize: 13,
              fontWeight: 650,
            }}
          />
        )
      )}
    </Box>
  </Box>
);

const SectionHeading = ({ title, subtitle }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: { xs: 'flex-start', md: 'flex-end' },
      flexDirection: { xs: 'column', md: 'row' },
      gap: 2,
      mb: 1.75,
    }}
  >
    <Typography
      component="h2"
      sx={{
        m: 0,
        color: colors.navy,
        fontSize: { xs: 20, md: 23 },
        fontWeight: 800,
        lineHeight: 1.25,
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        sx={{
          m: 0,
          color: colors.muted,
          fontSize: 14,
          maxWidth: 650,
          textAlign: { xs: 'left', md: 'right' },
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const KPICard = ({ label, value, note, variant }) => {
  const backgroundByVariant = {
    brand: `linear-gradient(180deg, #ffffff, ${colors.softBlue})`,
    good: `linear-gradient(180deg, #ffffff, ${colors.softGreen})`,
    warn: `linear-gradient(180deg, #ffffff, ${colors.softOrange})`,
  };

  return (
    <StyledCard sx={{ background: backgroundByVariant[variant] || colors.surface }}>
      <Typography
        sx={{
          color: colors.muted,
          fontSize: 12,
          letterSpacing: '.065em',
          textTransform: 'uppercase',
          fontWeight: 750,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 1,
          color: colors.navy,
          fontSize: { xs: 30, md: 34 },
          lineHeight: 1,
          fontWeight: 800,
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ mt: 1, color: colors.muted, fontSize: 13 }}>{note}</Typography>
    </StyledCard>
  );
};

const Legend = () => (
  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', color: colors.muted, fontSize: 12 }}>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', background: colors.brand }} />
      Vidisky
    </Box>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', background: colors.uniview }} />
      Uniview built-in
    </Box>
  </Box>
);

const ChartTitle = ({ title, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.5,
      mb: 2.25,
      flexDirection: { xs: 'column', sm: 'row' },
    }}
  >
    <Typography component="h3" sx={{ m: 0, color: colors.navy, fontSize: 17, fontWeight: 750 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const ChartFrame = ({ label, viewBox, children, wide = false }) => (
  <Box
    role="img"
    aria-label={label}
    sx={{
      position: 'relative',
      border: '1px solid #e7ecf4',
      background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
      borderRadius: '16px',
      p: { xs: '10px 8px 8px', sm: '14px 14px 10px' },
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '0 auto auto 0',
        width: '100%',
        height: 3,
        background: `linear-gradient(90deg, ${colors.brand}, #7bb9ff 58%, transparent 100%)`,
        opacity: 0.9,
      },
    }}
  >
    <Box
      component="svg"
      viewBox={viewBox}
      aria-hidden="true"
      sx={{
        display: 'block',
        width: '100%',
        height: 'auto',
        minHeight: { xs: 220, sm: wide ? 250 : 240 },
        overflow: 'visible',
        '& text': {
          fontFamily: 'inherit',
        },
        '& .chart-tick': {
          fill: colors.chartTick,
          fontSize: { xs: 10, sm: 11 },
          fontWeight: 600,
        },
        '& .chart-label': {
          fill: colors.chartLabel,
          fontSize: { xs: 10, sm: 12 },
          fontWeight: 700,
        },
        '& .chart-value': {
          fill: colors.navy,
          fontSize: { xs: 11, sm: 13 },
          fontWeight: 850,
        },
        '& .chart-subvalue': {
          fill: '#7b8495',
          fontSize: 10,
          fontWeight: 650,
        },
      }}
    >
      {children}
    </Box>
  </Box>
);

const ChartCallout = ({ number, title, detail }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      alignItems: 'center',
      gap: 1.25,
      mt: 1.5,
      p: '11px 12px',
      borderRadius: '12px',
      background: '#f6f9ff',
      border: '1px solid #e1e9f8',
    }}
  >
    <Box
      sx={{
        minWidth: 48,
        textAlign: 'center',
        px: 1,
        py: 0.875,
        borderRadius: '10px',
        color: colors.brand,
        background: colors.softBlue,
        fontSize: 16,
        fontWeight: 850,
      }}
    >
      {number}
    </Box>
    <Box>
      <Typography component="strong" sx={{ display: 'block', color: colors.navy, fontSize: 13 }}>
        {title}
      </Typography>
      <Typography component="span" sx={{ color: colors.muted, fontSize: 12 }}>
        {detail}
      </Typography>
    </Box>
  </Box>
);

const GridLines = ({ x1, x2, yValues }) => (
  <>
    {yValues.map((y) => (
      <line key={y} stroke="#e8edf5" strokeWidth="1" x1={x1} y1={y} x2={x2} y2={y} />
    ))}
  </>
);

const PercentAxis = ({ x, y1, y2, baselineY, tickX, ticks }) => (
  <>
    <line stroke="#cbd3df" strokeWidth="1.2" x1={x} y1={y1} x2={x} y2={y2} />
    <line stroke="#cbd3df" strokeWidth="1.2" x1={x} y1={baselineY} x2={tickX} y2={baselineY} />
    {ticks.map((tick) => (
      <text key={tick.label} className="chart-tick" x={x - 10} y={tick.y} textAnchor="end">
        {tick.label}
      </text>
    ))}
  </>
);

const OverallDetectionChart = () => (
  <ChartFrame label="Overall detection rate: Vidisky 96.3 percent and Uniview built-in 80.7 percent" viewBox="0 0 520 300">
    <GridLines x1="58" x2="490" yValues={[238, 194, 150, 106, 62, 18]} />
    <PercentAxis
      x={58}
      y1={18}
      y2={238}
      baselineY={238}
      tickX={490}
      ticks={[
        { label: '0%', y: 242 },
        { label: '20%', y: 198 },
        { label: '40%', y: 154 },
        { label: '60%', y: 110 },
        { label: '80%', y: 66 },
        { label: '100%', y: 22 },
      ]}
    />
    <rect fill={colors.brand} x="135" y="26.1" width="105" height="211.9" rx="8" />
    <rect fill={colors.uniview} x="310" y="60.5" width="105" height="177.5" rx="8" />
    <text className="chart-value" x="187.5" y="18" textAnchor="middle">96.3%</text>
    <text className="chart-value" x="362.5" y="52" textAnchor="middle">80.7%</text>
    <text className="chart-label" x="187.5" y="260" textAnchor="middle">Vidisky</text>
    <text className="chart-label" x="362.5" y="260" textAnchor="middle">Uniview built-in</text>
    <text className="chart-subvalue" x="187.5" y="278" textAnchor="middle">155 / 161 detected</text>
    <text className="chart-subvalue" x="362.5" y="278" textAnchor="middle">130 / 161 detected</text>
  </ChartFrame>
);

const MissedActivitiesChart = () => (
  <ChartFrame label="Missed activities: Vidisky 6 and Uniview built-in 31" viewBox="0 0 520 300">
    <GridLines x1="150" x2="480" yValues={[62, 116, 170, 224]} />
    <line stroke="#cbd3df" strokeWidth="1.2" x1="150" y1="42" x2="150" y2="240" />
    <line stroke="#cbd3df" strokeWidth="1.2" x1="150" y1="240" x2="480" y2="240" />
    {[
      { label: '0', x: 150 },
      { label: '10', x: 244 },
      { label: '20', x: 338 },
      { label: '30', x: 432 },
    ].map((tick) => (
      <text key={tick.label} className="chart-tick" x={tick.x} y="260" textAnchor="middle">
        {tick.label}
      </text>
    ))}
    {[244, 338, 432].map((x) => (
      <line key={x} stroke="#e8edf5" strokeWidth="1" x1={x} y1="42" x2={x} y2="240" />
    ))}
    <text className="chart-label" x="138" y="99" textAnchor="end">Vidisky</text>
    <rect fill={colors.brand} x="150" y="76" width="56.6" height="38" rx="8" />
    <text className="chart-value" x="218" y="100">6</text>
    <text className="chart-label" x="138" y="176" textAnchor="end">Uniview built-in</text>
    <rect fill={colors.uniview} x="150" y="153" width="292.3" height="38" rx="8" />
    <text className="chart-value" x="454" y="177">31</text>
  </ChartFrame>
);

const IntrusionPerformanceChart = () => (
  <ChartFrame label="Intrusion detection: Vidisky 97.9 percent and Uniview built-in 81.4 percent" viewBox="0 0 920 320" wide>
    <GridLines x1="72" x2="870" yValues={[250, 204, 158, 112, 66, 20]} />
    <PercentAxis
      x={72}
      y1={20}
      y2={250}
      baselineY={250}
      tickX={870}
      ticks={[
        { label: '0%', y: 254 },
        { label: '20%', y: 208 },
        { label: '40%', y: 162 },
        { label: '60%', y: 116 },
        { label: '80%', y: 70 },
        { label: '100%', y: 24 },
      ]}
    />
    <rect fill={colors.brand} x="250" y="24.8" width="150" height="225.2" rx="9" />
    <rect fill={colors.uniview} x="535" y="62.8" width="150" height="187.2" rx="9" />
    <text className="chart-value" x="325" y="16" textAnchor="middle">97.9%</text>
    <text className="chart-value" x="610" y="54" textAnchor="middle">81.4%</text>
    <text className="chart-label" x="325" y="274" textAnchor="middle">Vidisky</text>
    <text className="chart-label" x="610" y="274" textAnchor="middle">Uniview built-in</text>
    <text className="chart-subvalue" x="325" y="294" textAnchor="middle">95 of 97 detected</text>
    <text className="chart-subvalue" x="610" y="294" textAnchor="middle">79 of 97 detected</text>
  </ChartFrame>
);

const IntrusionBreakdownChart = () => (
  <ChartFrame label="Intrusion detection rates by camera and lighting condition" viewBox="0 0 980 360" wide>
    <GridLines x1="70" x2="930" yValues={[278, 226, 174, 122, 70, 18]} />
    <PercentAxis
      x={70}
      y1={18}
      y2={278}
      baselineY={278}
      tickX={930}
      ticks={[
        { label: '0%', y: 282 },
        { label: '20%', y: 230 },
        { label: '40%', y: 178 },
        { label: '60%', y: 126 },
        { label: '80%', y: 74 },
        { label: '100%', y: 22 },
      ]}
    />
    <rect fill={colors.brand} x="145" y="18" width="76" height="260" rx="7" />
    <rect fill={colors.uniview} x="229" y="51.3" width="76" height="226.7" rx="7" />
    <text className="chart-value" x="183" y="12" textAnchor="middle">100.0%</text>
    <text className="chart-value" x="267" y="44" textAnchor="middle">87.2%</text>
    <rect fill={colors.brand} x="430" y="23.2" width="76" height="254.8" rx="7" />
    <rect fill={colors.uniview} x="514" y="76.2" width="76" height="201.8" rx="7" />
    <text className="chart-value" x="468" y="16" textAnchor="middle">98.0%</text>
    <text className="chart-value" x="552" y="69" textAnchor="middle">77.6%</text>
    <rect fill={colors.brand} x="715" y="46.9" width="76" height="231.1" rx="7" />
    <rect fill={colors.uniview} x="799" y="75.7" width="76" height="202.3" rx="7" />
    <text className="chart-value" x="753" y="40" textAnchor="middle">88.9%</text>
    <text className="chart-value" x="837" y="69" textAnchor="middle">77.8%</text>
    <text className="chart-label" x="225" y="304" textAnchor="middle">Tri-Guard 1.0 - Day</text>
    <text className="chart-subvalue" x="225" y="322" textAnchor="middle">IPC3618SB</text>
    <text className="chart-label" x="510" y="304" textAnchor="middle">Tri-Guard 2.0 - Day</text>
    <text className="chart-subvalue" x="510" y="322" textAnchor="middle">IPC3638SS</text>
    <text className="chart-label" x="795" y="304" textAnchor="middle">Tri-Guard 2.0 - Night</text>
    <text className="chart-subvalue" x="795" y="322" textAnchor="middle">IPC3638SS</text>
  </ChartFrame>
);

const PersonVehicleChart = () => (
  <ChartFrame label="Separate person and vehicle detection rates" viewBox="0 0 980 360" wide>
    <GridLines x1="70" x2="930" yValues={[278, 226, 174, 122, 70, 18]} />
    <PercentAxis
      x={70}
      y1={18}
      y2={278}
      baselineY={278}
      tickX={930}
      ticks={[
        { label: '0%', y: 282 },
        { label: '20%', y: 230 },
        { label: '40%', y: 178 },
        { label: '60%', y: 126 },
        { label: '80%', y: 74 },
        { label: '100%', y: 22 },
      ]}
    />
    <rect fill={colors.brand} x="145" y="35.4" width="76" height="242.6" rx="7" />
    <rect fill={colors.uniview} x="229" y="87.4" width="76" height="190.6" rx="7" />
    <text className="chart-value" x="183" y="28" textAnchor="middle">93.3%</text>
    <text className="chart-value" x="267" y="80" textAnchor="middle">73.3%</text>
    <rect fill={colors.brand} x="430" y="31.8" width="76" height="246.2" rx="7" />
    <rect fill={colors.uniview} x="514" y="31.8" width="76" height="246.2" rx="7" />
    <text className="chart-value" x="468" y="24" textAnchor="middle">94.7%</text>
    <text className="chart-value" x="552" y="24" textAnchor="middle">94.7%</text>
    <rect fill={colors.brand} x="715" y="34.1" width="76" height="243.9" rx="7" />
    <rect fill={colors.uniview} x="799" y="70.8" width="76" height="207.2" rx="7" />
    <text className="chart-value" x="753" y="27" textAnchor="middle">93.8%</text>
    <text className="chart-value" x="837" y="64" textAnchor="middle">79.7%</text>
    <text className="chart-label" x="225" y="306" textAnchor="middle">Person</text>
    <text className="chart-subvalue" x="225" y="324" textAnchor="middle">45 activities</text>
    <text className="chart-label" x="510" y="306" textAnchor="middle">Vehicle</text>
    <text className="chart-subvalue" x="510" y="324" textAnchor="middle">19 activities</text>
    <text className="chart-label" x="795" y="306" textAnchor="middle">Mode total</text>
    <text className="chart-subvalue" x="795" y="324" textAnchor="middle">64 activities</text>
  </ChartFrame>
);

const MiniMetric = ({ label, value }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: '13px',
      background: '#f8fafc',
      border: `1px solid ${colors.line}`,
    }}
  >
    <Typography
      component="span"
      sx={{
        color: colors.muted,
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '.05em',
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
    <Typography component="strong" sx={{ display: 'block', mt: 0.5, color: colors.navy, fontSize: 20 }}>
      {value}
    </Typography>
  </Box>
);

const MetricsGrid = ({ items }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
      gap: 1.25,
      mt: 2.25,
    }}
  >
    {items.map((item) => (
      <MiniMetric key={item.label} label={item.label} value={item.value} />
    ))}
  </Box>
);

const ScoreRow = ({ label, value, color = 'brand' }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '88px 1fr 54px', sm: '110px 1fr 64px' },
      alignItems: 'center',
      gap: 1.25,
      my: 1.5,
      fontSize: 13,
    }}
  >
    <Typography component="span" sx={{ color: colors.ink, fontSize: 13 }}>
      {label}
    </Typography>
    <Box sx={{ height: 10, background: '#eef1f6', borderRadius: '999px', overflow: 'hidden' }}>
      <Box
        sx={{
          height: '100%',
          width: `${value}%`,
          borderRadius: 'inherit',
          background:
            color === 'brand'
              ? `linear-gradient(90deg, ${colors.brand}, ${colors.brandLight})`
              : 'linear-gradient(90deg, #7e889c, #a6afbf)',
        }}
      />
    </Box>
    <Typography component="strong" sx={{ color: colors.navy, fontSize: 13, textAlign: 'right' }}>
      {value}%
    </Typography>
  </Box>
);

const Insight = ({ icon, title, description }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1.75,
      alignItems: 'flex-start',
      p: 2,
      mt: 2,
      borderRadius: '14px',
      background: '#f9fbff',
      border: '1px solid #dfe9fa',
    }}
  >
    <Box
      sx={{
        flex: '0 0 34px',
        width: 34,
        height: 34,
        borderRadius: '11px',
        display: 'grid',
        placeItems: 'center',
        background: colors.softBlue,
        color: colors.brand,
        fontWeight: 900,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography component="strong" sx={{ display: 'block', color: colors.navy, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography sx={{ m: 0, color: colors.muted, fontSize: 13 }}>{description}</Typography>
    </Box>
  </Box>
);

const ClassCard = ({ badge, badgeVariant, title, subtitle, vidisky, uniview, insight }) => (
  <StyledCard
    sx={{
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '0 0 auto',
        height: 4,
        background: `linear-gradient(90deg, ${colors.brand}, #8fc4ff)`,
      },
    }}
  >
    <Badge label={badge} variant={badgeVariant} />
    <Typography component="h3" sx={{ mt: 1.25, mb: 0.25, color: colors.navy, fontSize: 20, fontWeight: 750 }}>
      {title}
    </Typography>
    <Typography sx={{ color: colors.muted, fontSize: 13, mb: 2 }}>{subtitle}</Typography>
    <ScoreRow label="Vidisky" value={vidisky} />
    <ScoreRow label="Uniview" value={uniview} color="uniview" />
    <Insight {...insight} />
  </StyledCard>
);

const StyledTable = ({ headers, rows }) => (
  <TableContainer
    component={Paper}
    sx={{
      overflowX: 'auto',
      border: `1px solid ${colors.line}`,
      borderRadius: '16px',
      boxShadow: 'none',
    }}
  >
    <Table sx={{ minWidth: 760 }}>
      <TableHead>
        <TableRow>
          {headers.map((header) => (
            <TableCell
              key={header.label}
              align={header.align || 'left'}
              sx={{
                p: '14px 16px',
                background: '#f7f9fc',
                color: '#4d5a70',
                fontSize: 11,
                letterSpacing: '.055em',
                textTransform: 'uppercase',
                fontWeight: 800,
                whiteSpace: 'nowrap',
                borderBottom: `1px solid ${colors.line}`,
              }}
            >
              {header.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            {row.cells.map((cell) => (
              <TableCell
                key={cell.key}
                align={cell.align || 'left'}
                sx={{
                  p: '14px 16px',
                  borderBottom: `1px solid ${colors.line}`,
                  color: colors.ink,
                  fontSize: 13,
                  verticalAlign: 'middle',
                  fontVariantNumeric: cell.align === 'right' ? 'tabular-nums' : 'normal',
                  '& strong': {
                    color: colors.navy,
                  },
                }}
              >
                {cell.content}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const CameraCard = ({ title, subtitle, badge, specs }) => (
  <StyledCard>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.75, mb: 1.75 }}>
      <Box>
        <Typography component="h3" sx={{ m: 0, color: colors.navy, fontSize: 18, fontWeight: 750 }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 0.5, color: colors.muted, fontSize: 13 }}>{subtitle}</Typography>
      </Box>
      <Badge label={badge} variant="neutral" />
    </Box>
    <Box component="ul" sx={{ display: 'grid', gap: 1, m: 0, p: 0, listStyle: 'none' }}>
      {specs.map((spec) => (
        <Box
          component="li"
          key={spec.label}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            pb: 1,
            borderBottom: `1px dashed ${colors.line}`,
            fontSize: 13,
            '&:last-child': {
              borderBottom: 0,
              pb: 0,
            },
          }}
        >
          <Typography component="span" sx={{ color: colors.muted, fontSize: 13 }}>
            {spec.label}
          </Typography>
          <Typography component="span" sx={{ color: colors.navy, fontWeight: 650, textAlign: 'right', fontSize: 13 }}>
            {spec.value}
          </Typography>
        </Box>
      ))}
    </Box>
  </StyledCard>
);

const NoteBox = ({ children }) => (
  <Box
    sx={{
      borderLeft: `4px solid ${colors.brand}`,
      background: '#f7faff',
      borderRadius: '0 14px 14px 0',
      p: '16px 18px',
      color: '#4d5a70',
      fontSize: 13,
      '& strong': {
        color: colors.navy,
      },
    }}
  >
    {children}
  </Box>
);

const ConclusionSection = () => (
  <Box
    component="section"
    sx={{
      mt: 3.75,
      background: 'linear-gradient(135deg, #0f2754 0%, #123e7c 100%)',
      color: '#fff',
      borderRadius: '22px',
      p: { xs: 2.5, md: 3.5 },
      boxShadow: reportShadow,
    }}
  >
    <Typography component="h2" sx={{ m: '0 0 14px', color: '#fff', fontSize: 25, fontWeight: 800 }}>
      Benchmark Conclusion
    </Typography>
    <Typography sx={{ m: 0, color: 'rgba(255,255,255,.82)', fontSize: 14, lineHeight: 1.6 }}>
      Across the evaluated scenarios, Vidisky produced materially higher event-level detection coverage than the
      Uniview built-in analytics, driven primarily by stronger person detection. Vehicle detection was equivalent
      in the separate class evaluation.
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mt: 2.5 }}>
      {[
        { value: '96.3%', label: 'Vidisky overall event detection' },
        { value: '+15.5 pp', label: 'Overall detection advantage' },
        { value: '80.6%', label: 'Reduction in missed activities' },
      ].map((item) => (
        <Box
          key={item.label}
          sx={{
            p: 1.75,
            borderRadius: '14px',
            background: 'rgba(255,255,255,.08)',
            border: '1px solid rgba(255,255,255,.12)',
          }}
        >
          <Typography component="strong" sx={{ display: 'block', mb: 0.375, fontSize: 20, fontWeight: 800 }}>
            {item.value}
          </Typography>
          <Typography component="span" sx={{ color: 'rgba(255,255,255,.72)', fontSize: 12 }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const intrusionRows = [
  {
    id: 'ipc3618-day',
    camera: 'IPC3618SB-ADF28KMC-I0',
    generation: 'Tri-Guard 1.0',
    condition: 'Day',
    activities: '39',
    vidisky: '39',
    vidiskyRate: '100.0%',
    uniview: '34',
    builtInRate: '87.2%',
  },
  {
    id: 'ipc3638-day',
    camera: 'IPC3638SS-ADF28KMC-I1',
    generation: 'Tri-Guard 2.0',
    condition: 'Day',
    activities: '49',
    vidisky: '48',
    vidiskyRate: '98.0%',
    uniview: '38',
    builtInRate: '77.6%',
  },
  {
    id: 'ipc3638-night',
    camera: 'IPC3638SS-ADF28KMC-I1',
    generation: 'Tri-Guard 2.0',
    condition: 'Night',
    activities: '9',
    vidisky: '8',
    vidiskyRate: '88.9%',
    uniview: '7',
    builtInRate: '77.8%',
  },
];

const evidenceRows = [
  {
    id: 'evidence-ipc3618-day',
    camera: 'IPC3618SB - Day',
    href: 'http://192.168.30.151:8070/storage/snapshots/10607/10607_person_20260807_193546_ba589621.jpg',
  },
  {
    id: 'evidence-ipc3638-day',
    camera: 'IPC3638SS - Day',
    href: 'http://192.168.30.151:8070/storage/snapshots/10608/10608_person_20260807_184654_d799db15.jpg',
  },
  {
    id: 'evidence-ipc3638-night',
    camera: 'IPC3638SS - Night',
    href: 'https://vdointel-ai-storage.s3.us-west-2.amazonaws.com/vidisky-cam-recordings/snapshots/10006/10006_car_20260808_034902_51b24ebf.jpg',
  },
];

const ClientBenchmarkReport = () => (
  <ReportContainer>
    <HeroSection />

    <Box component="section" sx={{ mt: 3.75 }}>
      <SectionHeading
        title="Executive Summary"
        subtitle="Event-level detection results across all evaluated modes and supplied test windows."
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard label="Vidisky detection rate" value="96.3%" note="155 of 161 activities detected" variant="brand" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard label="Uniview built-in rate" value="80.7%" note="130 of 161 activities detected" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard label="Detection advantage" value="+15.5 pp" note="Vidisky vs built-in analytics" variant="good" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard label="Missed activities" value="6 vs 31" note="Vidisky vs Uniview built-in" variant="warn" />
        </Grid>
      </Grid>
    </Box>

    <Grid component="section" container spacing={2} sx={{ mt: 1.75 }}>
      <Grid item xs={12} md={6}>
        <StyledCard>
          <ChartTitle title="Overall Detection Rate">
            <Legend />
          </ChartTitle>
          <OverallDetectionChart />
          <ChartCallout
            number="+25"
            title="Additional activities detected by Vidisky"
            detail="Overall detection advantage: +15.5 percentage points."
          />
        </StyledCard>
      </Grid>
      <Grid item xs={12} md={6}>
        <StyledCard>
          <ChartTitle title="Missed Activities">
            <Badge label="Lower is better" variant="good" />
          </ChartTitle>
          <MissedActivitiesChart />
          <ChartCallout
            number="-80.6%"
            title="Fewer missed activities with Vidisky"
            detail="Miss reduction calculated relative to Uniview built-in analytics."
          />
        </StyledCard>
      </Grid>
    </Grid>

    <Box component="section" sx={{ mt: 3.75 }}>
      <SectionHeading
        title="Evaluation Mode 1 - Intrusion Detection"
        subtitle="Person and vehicle activities are treated as one combined intrusion class."
      />
      <StyledCard>
        <ChartTitle title="Intrusion Detection Performance">
          <Badge label="97 activities" variant="brand" />
        </ChartTitle>
        <IntrusionPerformanceChart />
        <MetricsGrid
          items={[
            { label: 'Ground truth', value: '97' },
            { label: 'Vidisky misses', value: '2' },
            { label: 'Built-in misses', value: '18' },
          ]}
        />
      </StyledCard>
    </Box>

    <Box component="section" sx={{ mt: 3.75 }}>
      <SectionHeading
        title="Intrusion Detection - Camera & Lighting Breakdown"
        subtitle="Breakdown of the combined person + vehicle intrusion test windows."
      />
      <StyledCard sx={{ mb: 2, p: { xs: 2, md: '20px 20px 16px' } }}>
        <ChartTitle title="Intrusion Detection Rate by Camera & Lighting">
          <Legend />
        </ChartTitle>
        <IntrusionBreakdownChart />
        <Typography sx={{ mt: 1, color: colors.muted, fontSize: 11 }}>
          Values represent event-level detection recall within each supplied intrusion test window.
        </Typography>
      </StyledCard>
      <StyledTable
        headers={[
          { label: 'Camera' },
          { label: 'Condition' },
          { label: 'Activities', align: 'right' },
          { label: 'Vidisky', align: 'right' },
          { label: 'Vidisky Rate', align: 'right' },
          { label: 'Uniview Built-in', align: 'right' },
          { label: 'Built-in Rate', align: 'right' },
        ]}
        rows={intrusionRows.map((row) => ({
          id: row.id,
          cells: [
            {
              key: 'camera',
              content: (
                <Box>
                  <strong>{row.camera}</strong>
                  <Typography component="span" sx={{ display: 'block', color: '#7b8495', fontSize: 12 }}>
                    {row.generation}
                  </Typography>
                </Box>
              ),
            },
            { key: 'condition', content: row.condition },
            { key: 'activities', content: row.activities, align: 'right' },
            { key: 'vidisky', content: row.vidisky, align: 'right' },
            { key: 'vidiskyRate', content: <strong>{row.vidiskyRate}</strong>, align: 'right' },
            { key: 'uniview', content: row.uniview, align: 'right' },
            { key: 'builtInRate', content: row.builtInRate, align: 'right' },
          ],
        }))}
      />
    </Box>

    <Box component="section" sx={{ mt: 3.75 }}>
      <SectionHeading
        title="Evaluation Mode 2 - Separate Person & Vehicle Detection"
        subtitle="Person and vehicle detections are evaluated independently to expose class-level behavior."
      />
      <StyledCard sx={{ mb: 2, p: { xs: 2, md: '20px 20px 16px' } }}>
        <ChartTitle title="Person & Vehicle Detection Rate">
          <Legend />
        </ChartTitle>
        <PersonVehicleChart />
        <Typography sx={{ mt: 1, color: colors.muted, fontSize: 11 }}>
          Person performance shows the largest separation (+20.0 percentage points); vehicle detection is equivalent in this evaluation.
        </Typography>
      </StyledCard>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ClassCard
            badge="Person"
            badgeVariant="brand"
            title="45 activities"
            subtitle="Class-level detection performance"
            vidisky={93.3}
            uniview={73.3}
            insight={{
              icon: '20',
              title: '+20.0 percentage points',
              description: 'Vidisky detected 42/45 person activities; Uniview built-in detected 33/45.',
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ClassCard
            badge="Vehicle"
            badgeVariant="neutral"
            title="19 activities"
            subtitle="Class-level detection performance"
            vidisky={94.7}
            uniview={94.7}
            insight={{
              icon: '=',
              title: 'Equivalent vehicle performance',
              description: 'Both systems detected 18 of 19 vehicle activities in this evaluation mode.',
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ClassCard
            badge="Mode total"
            badgeVariant="good"
            title="64 activities"
            subtitle="Person + vehicle totals"
            vidisky={93.8}
            uniview={79.7}
            insight={{
              icon: '+',
              title: '9 additional detections',
              description: 'Vidisky detected 60/64 activities compared with 51/64 for the camera analytics.',
            }}
          />
        </Grid>
      </Grid>
    </Box>

    <Box component="section" sx={{ mt: 3.75 }}>
      <SectionHeading
        title="Camera & Firmware Context"
        subtitle="Firmware status is shown as deployment context and is not treated as proof of causation for analytics performance."
      />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CameraCard
            title="IPC3618SB-ADF28KMC-I0"
            subtitle="2021 model - Tri-Guard 1.0 - 8 MP / 4K UHD"
            badge="8 upgrades missed"
            specs={[
              { label: 'Resolution', value: '3840 x 2160' },
              { label: 'Current software', value: 'GIPC-B6203.13.7.240429' },
              { label: 'Latest software', value: 'GIPC-B6203.17.7.260618' },
              { label: 'Intrusion test window', value: '60 min - Day' },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CameraCard
            title="IPC3638SS-ADF28KMC-I1"
            subtitle="2024 model - Tri-Guard 2.0 - 8 MP / 4K UHD"
            badge="4 upgrades missed"
            specs={[
              { label: 'Resolution', value: '3840 x 2160' },
              { label: 'Current software', value: 'GIPC-B6213.5.7.250508' },
              { label: 'Latest software', value: 'GIPC-B6213.6.8.260618' },
              { label: 'Intrusion test windows', value: '60 min Day + 60 min Night' },
            ]}
          />
        </Grid>
      </Grid>
    </Box>

    <Box component="section" sx={{ mt: 3.75 }}>
      <SectionHeading title="Evidence References" subtitle="Snapshot references supplied with the benchmark data." />
      <StyledCard>
        <StyledTable
          headers={[{ label: 'Camera / condition' }, { label: 'Reference' }]}
          rows={evidenceRows.map((row) => ({
            id: row.id,
            cells: [
              { key: 'camera', content: <strong>{row.camera}</strong> },
              {
                key: 'reference',
                content: (
                  <Link
                    href={row.href}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ color: colors.brand, textDecoration: 'none', fontWeight: 650, '&:hover': { textDecoration: 'underline' } }}
                  >
                    Open supplied snapshot
                  </Link>
                ),
              },
            ],
          }))}
        />
      </StyledCard>
    </Box>

    <Box component="section" sx={{ mt: 3.75 }}>
      <SectionHeading
        title="Metric Scope"
        subtitle="This report measures event-level detection performance from the supplied ground-truth activity counts."
      />
      <NoteBox>
        <strong>What is measured:</strong> activity detection rate / event recall, detected activities, missed activities,
        class-level person and vehicle recall, camera and lighting breakdowns.
        <br />
        <br />
        <strong>Not yet measured:</strong> Precision, F1 score, IoU, mAP@50, mAP@50:95 and bounding-box localization
        accuracy require false-positive logging and frame-level ground-truth bounding-box annotations.
      </NoteBox>
    </Box>

    <ConclusionSection />

    <Box
      component="footer"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2.5,
        mt: 3.25,
        pt: 2.25,
        px: 0.5,
        color: '#7b8495',
        fontSize: 12,
        borderTop: `1px solid ${colors.line}`,
        flexDirection: { xs: 'column', sm: 'row' },
      }}
    >
      <Typography component="span" sx={{ color: 'inherit', fontSize: 'inherit' }}>
        Uniview vs Vidisky Analytics Benchmark
      </Typography>
      <Typography component="span" sx={{ color: 'inherit', fontSize: 'inherit' }}>
        Client-facing evaluation report
      </Typography>
    </Box>
  </ReportContainer>
);

export default function Report() {
  return (
    <Page title="Client Benchmark Report">
      <Container maxWidth="xxl">
        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4">Client Benchmark Report</Typography>
          </Box>
          <Breadcrumbs separator="/" aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/" sx={{ fontSize: '0.875rem' }}>
              Dashboard
            </Link>
            <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>
              Client Benchmark Report
            </Typography>
          </Breadcrumbs>
        </Box>

        <ClientBenchmarkReport />
      </Container>
    </Page>
  );
}
